import { ALL_VALUE } from '@/pages/dashboard/_config/constant';
import type { ICascadeFilterValue, TCascadeFilterOption } from '@/pages/dashboard/components/CascadeFilterComponent/_type';
import type { IAiAnalysisScope, IAiScopeTabItem } from '../types';

export const AI_SCOPE_FILTER_KEY = 'ai_analysis_scope';
export const AI_SCOPE_PARENT_KEY = 'tab_ids';
export const AI_SCOPE_CHILD_KEY = 'chart_ids';

export interface IAiScopeOptionsResult {
  options: TCascadeFilterOption[];
  allTaskIds: string[];
  chartToTabMap: Record<string, string>;
}

const getChartDisplayName = (
  chart: { name?: string; nameCn?: string; },
  isEn: boolean,
): string => {
  if (isEn) {
    return String(chart.name || chart.nameCn || '');
  }
  return String(chart.nameCn || chart.name || '');
};

const getTabDisplayName = (
  tab: IAiScopeTabItem,
  isEn: boolean,
): string => {
  const tabId = String(tab.tabId || '');
  if (tabId === '-1') {
    return isEn ? 'Tab' : '选项卡';
  }
  const preferredName = isEn ? tab.tabName : (tab.tabNameCN || tab.tabName);
  return String(preferredName || tab.tabName || tabId);
};

export const buildAiScopeOptions = (
  scopeTabs: IAiScopeTabItem[],
  isEn: boolean,
): IAiScopeOptionsResult => {
  const allTaskIdsSet = new Set<string>();
  const chartToTabMap: Record<string, string> = {};
  const options: TCascadeFilterOption[] = [];
  for (const tab of scopeTabs) {
    const tabId = String(tab.tabId || '');
    if (!tabId) {
      continue;
    }
    const children = [];
    for (const chart of tab.charts || []) {
      const chartId = String(chart.chartId || '');
      if (!chartId || allTaskIdsSet.has(chartId)) {
        continue;
      }
      allTaskIdsSet.add(chartId);
      chartToTabMap[chartId] = tabId;
      const chartName = getChartDisplayName(chart, isEn);
      children.push({
        value: chartId,
        text: chartName,
        textCN: chartName,
      });
    }
    if (!children.length) {
      continue;
    }
    const tabName = getTabDisplayName(tab, isEn);
    options.push({
      value: tabId,
      text: tabName,
      textCN: tabName,
      children,
    });
  }
  return {
    options,
    allTaskIds: Array.from(allTaskIdsSet),
    chartToTabMap,
  };
};

export const normalizeAnalysisScope = (
  analysisScope: IAiAnalysisScope | undefined,
  allTaskIds: string[],
): IAiAnalysisScope => {
  if (!analysisScope) {
    return {
      type: 'all',
      taskIds: allTaskIds,
    };
  }
  if (analysisScope.type === 'all') {
    return {
      type: 'all',
      taskIds: allTaskIds,
    };
  }
  const validTaskIdSet = new Set(allTaskIds);
  const validTaskIds = (analysisScope.taskIds || []).filter((taskId) => validTaskIdSet.has(taskId));
  return {
    type: 'chart',
    taskIds: validTaskIds,
  };
};

export const convertScopeToCascadeValue = (
  analysisScope: IAiAnalysisScope,
  chartToTabMap: Record<string, string>,
): ICascadeFilterValue => {
  if (analysisScope.type === 'all') {
    return {
      [AI_SCOPE_PARENT_KEY]: [],
      [AI_SCOPE_CHILD_KEY]: [ALL_VALUE],
      type: AI_SCOPE_CHILD_KEY,
    };
  }
  const tabIds = new Set<string>();
  for (const taskId of analysisScope.taskIds) {
    const tabId = chartToTabMap[taskId];
    if (tabId) {
      tabIds.add(tabId);
    }
  }
  return {
    [AI_SCOPE_PARENT_KEY]: Array.from(tabIds),
    [AI_SCOPE_CHILD_KEY]: analysisScope.taskIds,
    type: AI_SCOPE_CHILD_KEY,
  };
};

export const convertCascadeValueToScope = (
  value: ICascadeFilterValue | null | undefined,
  allTaskIds: string[],
): IAiAnalysisScope => {
  if (!value) {
    return {
      type: 'all',
      taskIds: allTaskIds,
    };
  }
  const selectedTaskIds = Array.isArray(value?.[AI_SCOPE_CHILD_KEY]) ? (value?.[AI_SCOPE_CHILD_KEY] as string[]) : [];
  if (selectedTaskIds.includes(ALL_VALUE)) {
    return {
      type: 'all',
      taskIds: allTaskIds,
    };
  }
  return {
    type: 'chart',
    taskIds: selectedTaskIds,
  };
};
